import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BranchContextService } from '@core/projects/branch-context.service';

/**
 * Guards the `projects/:projectId` route: the branchName query parameter must exist
 * in the project's list of branches. If the branch does not exist, redirects to
 * the project's default branch (or the first available branch as fallback).
 *
 * Also handles the case where branchName is missing (undefined/empty) by redirecting
 * to the default branch (AMONE-93 Fix 3).
 *
 * This guard complements `projectExistsGuard` which validates the projectId.
 * It should be applied as a `canActivate` guard AFTER `projectExistsGuard` (canMatch).
 */
export const branchExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const branchContext = inject(BranchContextService);

  const projectId = route.paramMap.get('projectId');
  const branchName = route.queryParamMap.get('branchName');

  // If projectId is missing, let other guards/logic handle it
  if (!projectId) {
    return true;
  }

  return branchContext.resolve(projectId, branchName).pipe(
    map(resolvedBranch => {
      if (!resolvedBranch) return false;
      if (resolvedBranch === branchName) return true;

      // Keep the requested resource/detail path; only repair the branch context.
      const target = router.parseUrl(state.url);
      target.queryParams = { ...target.queryParams, branchName: resolvedBranch };
      return target;
    }),
    catchError(() => {
      // If project cannot be loaded (e.g., 404), deny the route
      return of(false);
    })
  );
};
